import {
  DEFAULT_COLOR_PRIMARY,
  DEFAULT_MARKDOWN_PLACEHOLDER,
  DEFAULT_COLOR_WHITE,
} from '../constants'
import { basePropertiesSchema } from '../base-schema-properties'
import {
  bgColorSchema,
  showAllSettingsSchema,
  titlePropertiesSchema,
  textColorSchema,
  textFontFamilySchema,
  buttonsPropertiesSchema,
  linkColorSchema,
  getButtonProperties,
  textAlignSchemaField,
} from '../common'

export default {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Tickets',
  description: 'Tickets',
  type: 'object',
  'ui:options': {
    onlyShowIfDependent: true,
  },
  dependencies: {
    tmp_showAllSettings: [
      'bgColor',
      'autoColsOnDesktop',
      'colsNumberOnDesktop',
      'itemsAlign',
      'textColor',
    ],
    title: ['titleAlign', 'hideOnEmbed'],
  },
  properties: {
    ...basePropertiesSchema,
    ...titlePropertiesSchema,
    subtitle: {
      title: 'Subtitle',
      type: 'string',
    },
    ...showAllSettingsSchema,
    ...bgColorSchema,
    autoColsOnDesktop: {
      title: 'Auto columns on desktop',
      type: 'boolean',
      default: true,
    },
    colsNumberOnDesktop: {
      title: 'Number of cols on desktop',
      type: 'number',
      default: 4,
      minimum: 2,
      maximum: 4,
      'ui:hidden': '{{parentFormData.autoColsOnDesktop}}',
    },
    itemsAlign: {
      ...textAlignSchemaField,
      title: 'Items align',
      'ui:widget': 'RadioWidget',
    },
    subtitleAlign: {
      ...titlePropertiesSchema.titleAlign,
      title: 'Subtitle align',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    subtitleWeight: {
      ...titlePropertiesSchema.titleWeight,
      title: 'Subtitle weight',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    subtitleStyle: {
      ...titlePropertiesSchema.titleStyle,
      title: 'Subtitle style',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    subtitleSizePx: {
      ...titlePropertiesSchema.titleSizePx,
      title: 'Subtitle size',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    subtitleColor: {
      ...titlePropertiesSchema.titleColor,
      title: 'Subtitle color',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    subtitleFontFamily: {
      ...textFontFamilySchema.textFontFamily,
      title: 'Subtitle font family',
      'ui:hidden': '{{parentFormData.tmp_showAllSettings === undefined}}',
    },
    categories: {
      type: 'array',
      title: 'A list of category cards',
      'ui:options': {
        showIndexNumber: true,
      },
      items: {
        title: 'Category card',
        type: 'object',
        required: ['title'],
        properties: {
          title: {
            title: 'Title',
            type: 'string',
            default: 'Category title',
          },
          color: {
            title: 'Card color',
            type: 'string',
            default: DEFAULT_COLOR_PRIMARY,
          },
          titleColor: {
            ...textColorSchema.textColor,
            title: 'Card title color',
            default: DEFAULT_COLOR_WHITE,
          },
          titleFontFamily: {
            ...textFontFamilySchema.textFontFamily,
            title: 'Card title font family',
          },
          textColor: {
            ...textColorSchema.textColor,
            title: 'Card text color',
          },
          textFontFamily: {
            ...textFontFamilySchema.textFontFamily,
            title: 'Card text font family',
          },
          isShowVat: {
            title: 'Is show VAT',
            type: 'boolean',
            default: true,
          },
          text: {
            title: 'Text',
            type: 'string',
            'ui:options': {
              placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
              type: 'textarea',
              rows: 6,
            },
          },
          aboutTicketText: {
            title: 'About ticket text',
            type: 'string',
            default: 'about ticket',
          },
          popupTitle: {
            title: 'Popup title',
            type: 'string',
            default: 'About ticket',
          },
          additionalInfo: {
            title: 'Additional info for popup',
            type: 'string',
            'ui:options': {
              placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
              type: 'textarea',
              rows: 6,
            },
          },
          isSubscriptionSection: {
            title: 'Show subscription button',
            description: 'Category should have no tickets for this feature',
            type: 'boolean',
            default: false,
          },
          subscribeButton: {
            type: 'array',
            items: {
              type: 'object',
              properties: getButtonProperties({
                text: 'Subscribe',
                url: '/pro',
              }),
            },
            title: 'Subscribe button (no subscription)',
            'ui:hidden': '{{parentFormData.isSubscriptionSection !== true }}',
            'ui:options': {
              showIndexNumber: false,
              addable: false,
              sortable: false,
              removable: false,
            },
            minItems: 1,
            maxItems: 1,
          },
          subscriptionActiveButton: {
            type: 'array',
            items: {
              type: 'object',
              properties: getButtonProperties({
                text: 'Subscription Active!',
                url: '/pro',
              }),
            },
            title: 'Subscribe button (subscription active)',
            'ui:hidden': '{{parentFormData.isSubscriptionSection !== true }}',
            'ui:options': {
              showIndexNumber: false,
              addable: false,
              sortable: false,
              removable: false,
            },
            minItems: 1,
            maxItems: 1,
          },
          tickets: {
            type: 'array',
            title: 'Linked tickets',
            'ui:options': {
              showIndexNumber: true,
            },
            items: {
              title: 'Ticket',
              type: 'object',
              required: ['label'],
              properties: {
                label: {
                  title: 'Name',
                  type: 'string',
                },
                value: {
                  title: 'Ticket id',
                  type: 'number',
                },
                maxTicketsCount: {
                  title: 'Max tickets count',
                  type: 'number',
                  default: 99,
                  minimum: 1,
                  maximum: 99,
                },
                freeTicketText: {
                  title: 'Free ticket price text',
                  type: 'string',
                  default: 'Free',
                },
              },
            },
          },
        },
      },
    },
    footerText: {
      title: 'Footer text',
      type: 'string',
      'ui:options': {
        placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
        type: 'textarea',
        rows: 6,
      },
      'ui:widget': 'EditorMarkdownWidget',
    },
    footerTextColor: {
      ...textColorSchema.textColor,
      title: 'Footer text color',
      default: undefined,
    },
    footerTextLinkColor: {
      ...linkColorSchema.linkColor,
      title: 'Footer text link color',
    },
    footerTextFontFamily: {
      ...textFontFamilySchema.textFontFamily,
      title: 'Footer text font family',
    },
    footerButtons: {
      ...buttonsPropertiesSchema.buttons,
      title: 'Footer buttons',
    },
    tmp_promoBannerNotice: {
      title: 'Promo banner',
      description:
        'Promo banner settings have moved to the separate “Floating banner” block. Create that block on the page to configure the banner.',
      type: 'object',
      properties: {},
    },
  },
}
